import { useQueryClient } from 'react-query';
import { getSchemaQueryKey, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaCareCreditIntegrationV1Service, serviceName } from '../service';
import { GeneratePatientDataPurlIO, ServiceQueries } from '../types';
import { useCareCreditIntegrationV1Queries } from './use-care-credit-integration-v1-queries';
import {
  UseCareCreditIntegrationV1QueryEndpointArgs,
  useCareCreditIntegrationV1Query,
} from './use-care-credit-integration-v1-query';

/**
 * A hook that returns a query for the `GeneratePatientDataPurl` endpoint.
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useGeneratePatientDataPurlQuery = <E = unknown, D = GeneratePatientDataPurlIO['output']>(
  args: UseCareCreditIntegrationV1QueryEndpointArgs<'GeneratePatientDataPurl', E, D>
) =>
  useCareCreditIntegrationV1Query({
    endpointName: 'GeneratePatientDataPurl',
    ...args,
  });

/**
 * A hook that returns an array of queries for the `GeneratePatientDataPurl` endpoint.
 * It takes an array of objects with the following properties:
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useGeneratePatientDataPurlQueries = <E = unknown, D = GeneratePatientDataPurlIO['output']>(
  argsArr: UseCareCreditIntegrationV1QueryEndpointArgs<'GeneratePatientDataPurl', E, D>[]
) =>
  useCareCreditIntegrationV1Queries(
    argsArr.map((args) => ({
      endpointName: 'GeneratePatientDataPurl',
      ...args,
    }))
  );

/**
 * A hook that returns a function to fetch the query for the `GeneratePatientDataPurl` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useFetchGeneratePatientDataPurl = () => {
  const queryClient = useQueryClient();

  const fetchGeneratePatientDataPurl = async <E = unknown, D = GeneratePatientDataPurlIO['output']>({
    request,
    options,
    httpOptions,
    requestKeysToOmitFromQueryKey,
    ...rest
  }: Omit<UseCareCreditIntegrationV1QueryEndpointArgs<'GeneratePatientDataPurl', E, D>, 'options'> & {
    options?: LimitedSchemaFetchQueryOptions<
      GeneratePatientDataPurlIO,
      E,
      D,
      ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'GeneratePatientDataPurl'>>
    >;
  }) => {
    const resolvedRequest = typeof request === 'function' ? request({}) : request;
    try {
      const response = await queryClient.fetchQuery<
        GeneratePatientDataPurlIO['output'],
        E,
        D,
        ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'GeneratePatientDataPurl'>>
      >(
        getSchemaQueryKey<ServiceQueries, 'GeneratePatientDataPurl'>({
          serviceName,
          endpointName: 'GeneratePatientDataPurl',
          request: resolvedRequest,
          httpOptions,
          requestKeysToOmitFromQueryKey,
        }),
        () => SchemaCareCreditIntegrationV1Service.GeneratePatientDataPurl(resolvedRequest, httpOptions),
        options
      );

      return response;
    } catch (error) {
      // We check it this way in case the fallbackDataOnError is nullish
      if ('fallbackDataOnError' in rest) {
        return rest.fallbackDataOnError as D;
      }
      throw error;
    }
  };

  return fetchGeneratePatientDataPurl;
};
